import type React from 'react'

/* ── Styles ──────────────────────────────────────────────────── */
export const s: Record<string, React.CSSProperties> = {
  root: { minHeight: '100vh' },
  loadRoot: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5rem', background: '#FAF6EF', color: '#888' },

  /* Preview */
  previewWrap: {
    background:   '#FAF6EF',
    borderRadius: 16,
    padding:      '20px 24px',
    border:       '1.5px solid #EFE7D7',
    display:      'inline-flex',
    flexDirection: 'column' as const,
    gap:          12,
  },
  previewGrid: {
    display:             'grid',
    gridTemplateColumns: 'repeat(5, 32px)',
    gridTemplateRows:    'repeat(5, 32px)',
    gap:                 3,
  },
  previewCell: {
    width:          32,
    height:         32,
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    fontSize:       '0.875rem',
    fontWeight:     700,
    fontFamily:     "'Courier New', monospace",
    userSelect:     'none' as const,
  },
  previewWordRow: { display: 'flex', gap: 12, alignItems: 'center' },
  previewWord:    { fontSize: '0.875rem', fontWeight: 700, color: '#6B4C3B', fontFamily: "'Courier New', monospace" },

  /* Play */
  playRoot: {
    height:        '100dvh',
    display:       'flex',
    flexDirection: 'column',
    overflow:      'hidden',
    background:    '#FAF6EF',
  },
  // Play body: measured container — fills all remaining vertical space
  playBody: {
    flex:        1,
    minHeight:   0,
    display:     'flex',
    flexDirection: 'column' as const,  // portrait default; overridden inline for landscape
    overflow:    'hidden',
  },

  // Portrait: word pills as a horizontally scrolling row at the top
  wordPillsRow: {
    display:    'flex',
    flexWrap:   'wrap' as const,
    gap:        8,
    padding:    '8px 16px',
    flexShrink: 0,
    overflowX:  'auto' as const,
    maxHeight:  160,
  },
  wordPill: {
    display:      'inline-flex',
    alignItems:   'center',
    padding:      '6px 16px',
    borderRadius: 999,
    fontSize:     '1.5rem',
    fontWeight:   700,
    fontFamily:   "'Courier New', monospace",
    whiteSpace:   'nowrap' as const,
    flexShrink:   0,
  },

  // Grid centred in its container
  gridCenter: {
    flex:           1,
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    overflow:       'hidden',
    padding:        '8px 16px',
  },

  // Instruction below grid
  instructionRow: {
    flexShrink: 0,
    padding:    '6px 16px 10px',
    textAlign:  'center' as const,
    height:     40,
    display:    'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  instruction: {
    fontSize:   '0.95rem',
    fontStyle:  'italic',
    color:      '#6B4C3B',
    fontWeight: 500,
  },

  // Landscape: left word list panel
  wordPanel: {
    flexShrink: 0,
    width:      240,
    display:    'flex',
    flexDirection: 'column' as const,
    overflow:   'hidden',
    padding:    '12px 0 12px 16px',
  },
  wordList: {
    flex:          1,
    display:       'flex',
    flexDirection: 'column',
    gap:           5,
    background:    '#FFFFFF',
    borderRadius:  14,
    padding:       '12px 10px',
    border:        '1.5px solid #EFE7D7',
    boxShadow:     '0 2px 10px rgba(27,67,50,0.06)',
    overflowY:     'auto' as const,
  },
  wordItem: {
    fontSize:      '1.5rem',
    fontWeight:    700,
    paddingLeft:   8,
    paddingTop:    4,
    paddingBottom: 4,
    borderRadius:  6,
    transition:    'all 0.3s ease',
    fontFamily:    "'Courier New', monospace",
    lineHeight:    1.5,
  },

  // Landscape: right panel (grid + instruction)
  landscapeRight: {
    flex:          1,
    minWidth:      0,
    display:       'flex',
    flexDirection: 'column' as const,
    overflow:      'hidden',
  },

  /* Grid */
  grid: {
    display:    'grid',
    userSelect: 'none' as const,
  },
  cell: {
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    fontFamily:     "'Courier New', monospace",
    fontWeight:     700,
    borderRadius:   6,
    cursor:         'pointer',
    transition:     'transform 0.1s ease, background 0.15s ease',
    padding:        0,
    position:       'relative' as const,
  },

  /* Complete */
  completeRoot: {
    minHeight:      '100vh',
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    padding:        '24px',
  },
  completeCard: {
    background:   '#FFFFFF',
    borderRadius: 28,
    padding:      '48px 40px',
    maxWidth:     480,
    width:        '100%',
    textAlign:    'center' as const,
    boxShadow:    '0 20px 64px rgba(27,67,50,0.16)',
    border:       '1.5px solid #EFE7D7',
  },
  completeEmoji:    { fontSize: '4rem', lineHeight: 1, marginBottom: 20 },
  completeTitle: {
    fontFamily:   "'Cormorant Garamond', serif",
    fontSize:     '2.8rem',
    fontWeight:   700,
    color:        '#1B4332',
    marginBottom: 10,
  },
  completeSubtitle: { fontSize: '1.05rem', color: '#6B4C3B', fontWeight: 500, marginBottom: 36 },
  completeStats: {
    display:       'flex',
    justifyContent: 'center',
    alignItems:    'center',
    gap:           24,
    background:    '#FAF6EF',
    borderRadius:  16,
    padding:       '24px',
    marginBottom:  36,
    border:        '1.5px solid #EFE7D7',
  },
  completeStat: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 },
  completeStatNum: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize:   '2rem',
    fontWeight: 700,
    color:      '#1B4332',
    lineHeight: 1,
  },
  completeStatLabel: {
    fontSize: '0.875rem',
    fontWeight:    700,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.07em',
    color:         '#9A8070',
  },
  completeStatDivider: { width: 1, height: 40, background: '#D8C9B8', borderRadius: 1 },
  completeActions:     { display: 'flex', flexDirection: 'column', gap: 12 },
  completeBtnPrimary: {
    padding:      '18px',
    fontSize:     '1.1rem',
    fontWeight:   800,
    color:        '#FFFFFF',
    background:   'linear-gradient(135deg, #1B4332, #2D6A4F)',
    borderRadius: 14,
    boxShadow:    '0 6px 20px rgba(27,67,50,0.2)',
    letterSpacing: '0.02em',
  },
  completeBtnSecondary: {
    padding:      '16px',
    fontSize:     '1rem',
    fontWeight:   700,
    color:        '#1B4332',
    background:   '#F0FAF4',
    border:       '2px solid #B7E4C7',
    borderRadius: 14,
  },
  completeBtnGhost: {
    padding:      '14px',
    fontSize:     '0.95rem',
    fontWeight:   600,
    color:        '#9A8070',
    background:   'transparent',
    borderRadius: 14,
  },
}
